export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const EUROPEAN_DATE_PATTERN = /^\d{2}-\d{2}-\d{4}$/;

const currentLocale: string = navigator.language || navigator.languages[0];

/**
 * Parsers that convert a trimmed cell string into a comparable timestamp.
 * Add new date/timestamp formats here; they are tried in order.
 */
type SortableDateParser = {
    name: string;
    parse: (trimmed: string) => number | null;
};

const toLocalDateSortValue = (year: number, month: number, day: number): number | null => {
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }
    return date.getTime();
};

const sortableDateParsers: SortableDateParser[] = [
    {
        name: "dd-MM-yyyy",
        parse: (trimmed) => {
            if (!EUROPEAN_DATE_PATTERN.test(trimmed)) {
                return null;
            }
            const [day, month, year] = trimmed.split("-").map(Number);
            return toLocalDateSortValue(year, month, day);
        },
    },
    {
        name: "yyyy-MM-dd",
        parse: (trimmed) => {
            if (!ISO_DATE_PATTERN.test(trimmed)) {
                return null;
            }
            const [year, month, day] = trimmed.split("-").map(Number);
            return toLocalDateSortValue(year, month, day);
        },
    },
];

export const parseSortableDateValue = (value: unknown): number | null => {
    if (value == null) {
        return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    for (const parser of sortableDateParsers) {
        const sortValue = parser.parse(trimmed);
        if (sortValue != null) {
            return sortValue;
        }
    }
    return null;
};

/**
 * Chronological compare when both values match a known date format.
 * Returns null when either value is not a recognized date (caller should fall back).
 */
export const compareSortableDateValues = (valA: unknown, valB: unknown): number | null => {
    const dateA = parseSortableDateValue(valA);
    const dateB = parseSortableDateValue(valB);
    if (dateA == null || dateB == null) {
        return null;
    }
    return dateA - dateB;
};

export const formatIsoDate = (value: Date): string => {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const formatFilenameSafeDate = (value: Date | string): string => {
    const date = value instanceof Date ? value : new Date(value);
    const resolved = Number.isNaN(date.getTime()) ? new Date() : date;
    const hours = `${resolved.getHours()}`.padStart(2, "0");
    const minutes = `${resolved.getMinutes()}`.padStart(2, "0");
    const seconds = `${resolved.getSeconds()}`.padStart(2, "0");
    return `${formatIsoDate(resolved)}_${hours}-${minutes}-${seconds}`;
};

export const normalizeIsoDate = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }
    const trimmed = `${value}`.trim();
    if (ISO_DATE_PATTERN.test(trimmed)) {
        return trimmed;
    }
    const datePart = trimmed.slice(0, 10);
    if (ISO_DATE_PATTERN.test(datePart)) {
        return datePart;
    }
    return undefined;
};

export const parseIsoDate = (value?: string | null): Date | null => {
    const normalized = normalizeIsoDate(value);
    if (!normalized) {
        return null;
    }
    const parsed = new Date(`${normalized}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isValidIsoDateRange = (startIso?: string, endIso?: string): boolean => {
    const start = parseIsoDate(startIso);
    const end = parseIsoDate(endIso);
    return Boolean(start && end && end >= start);
};

export const maxIsoDate = (firstIso?: string | null, secondIso?: string | null): Date | undefined => {
    const first = parseIsoDate(firstIso);
    const second = parseIsoDate(secondIso);
    if (!first) {
        return second ?? undefined;
    }
    if (!second) {
        return first;
    }
    return first >= second ? first : second;
};

export const minIsoDate = (firstIso?: string | null, secondIso?: string | null): Date | undefined => {
    const first = parseIsoDate(firstIso);
    const second = parseIsoDate(secondIso);
    if (!first) {
        return second ?? undefined;
    }
    if (!second) {
        return first;
    }
    return first <= second ? first : second;
};

export const clampIsoDateBetween = (
    isoDate: string | undefined,
    rangeStartIso?: string,
    rangeEndIso?: string,
): string | undefined => {
    const date = parseIsoDate(isoDate);
    const rangeStart = parseIsoDate(rangeStartIso);
    const rangeEnd = parseIsoDate(rangeEndIso);
    if (!date || !rangeStart || !rangeEnd) {
        return normalizeIsoDate(isoDate);
    }
    let clamped = date;
    if (clamped < rangeStart) {
        clamped = rangeStart;
    }
    if (clamped > rangeEnd) {
        clamped = rangeEnd;
    }
    return formatIsoDate(clamped);
};

export interface IsoDatePeriodRange {
    start: string;
    end: string;
}

export const splitIsoPeriodIntoRanges = (startIso: string, endIso: string, count: number): IsoDatePeriodRange[] => {
    const start = parseIsoDate(startIso);
    const end = parseIsoDate(endIso);
    if (!start || !end || count < 1 || end < start) {
        return [];
    }

    const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    const totalDays = Math.floor((endUtc - startUtc) / (24 * 60 * 60 * 1000)) + 1;
    const baseSize = Math.floor(totalDays / count);
    const remainder = totalDays % count;

    const ranges: IsoDatePeriodRange[] = [];
    let cursor = new Date(start.getTime());

    for (let index = 0; index < count; index += 1) {
        const segmentDays = baseSize + (index < remainder ? 1 : 0);
        const segmentEnd = new Date(cursor.getTime());
        segmentEnd.setDate(segmentEnd.getDate() + segmentDays - 1);
        ranges.push({
            start: formatIsoDate(cursor),
            end: formatIsoDate(segmentEnd),
        });
        const nextStart = new Date(segmentEnd.getTime());
        nextStart.setDate(nextStart.getDate() + 1);
        cursor = nextStart;
    }

    return ranges;
};

export const isDateWithinIsoPeriod = (
    startIso?: string | null,
    endIso?: string | null,
    referenceDate: Date = new Date(),
): boolean => {
    const start = parseIsoDate(startIso);
    const end = parseIsoDate(endIso);
    if (!start || !end) {
        return false;
    }
    const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const dayEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
    return referenceDate >= dayStart && referenceDate <= dayEnd;
};

export const formatDisplayDate = (isoDate: string): string => {
    const parsed = parseIsoDate(isoDate);
    if (!parsed) {
        return isoDate;
    }
    return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const dateUtilFull = (dateString: string): string => {
    if (!currentLocale) return "nl-NL";
    if (!dateString) return "12:00";
    const date = new Date(dateString);
    const formattedDate = new Intl.DateTimeFormat(currentLocale, {
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
    return formattedDate.replace(" ", " ").replace("uur", "").replace("om", "");
};
